import * as React from "react";
import { useMemo } from "react";
import { NotFoundPage } from "@/containers";
import { CoachSettings, EnvValues, PricePlanTier, WebComponents, WebSettings } from "@/interfaces";
import { useAccountSettings, usePlayerSettings } from "@/utils";
import { SocialCoachSession } from "@/services/bedrock";
import { Page } from "@/Routes";

// 1. CoachComponentGuard
interface CoachComponentGuardProps {
  componentName: WebComponents | WebComponents[];
  Component: any;
  routeProps: any;
  envValues: EnvValues;
}

export const CoachComponentGuard: React.FC<CoachComponentGuardProps> = ({
  componentName,
  Component,
  routeProps,
  envValues,
}) => {
  const { hasReadPermission } = useAccountSettings();
  const hasPermissions = hasReadPermission(componentName);
  return hasPermissions ? (
    <Page {...routeProps} envValues={envValues} Component={Component} />
  ) : (
    <Page {...routeProps} envValues={envValues} Component={NotFoundPage} />
  );
};

// 2. ScriptAccessGuard
interface ScriptAccessProps {
  routeProps: any;
  Component: any;
  session?: SocialCoachSession;
  additionalSettings?: CoachSettings[];
  envValues: EnvValues;
}

export const ScriptAccessGuard: React.FC<ScriptAccessProps> = ({
  routeProps,
  Component,
  session,
  additionalSettings,
  envValues,
}) => {
  const { hasAccountSetting } = useAccountSettings();
  const { hasSetting } = usePlayerSettings(session?.playerId);

  const hasScriptAccess = useMemo(() => {
    return (
      session?.userRole == "ADMIN" ||
      session?.userRole == "EDITOR" ||
      (session?.userRole != "PLAYER" && hasAccountSetting(WebSettings.MANAGE_SCRIPT_LIBRARY)) ||
      (session?.userRole == "PLAYER" &&
        (session?.subscription?.tier == PricePlanTier.PLAYER_PLUS ||
          hasAccountSetting(WebSettings.ACCESS_SCRIPT_LIBRARY)))
    );
  }, [hasAccountSetting, session?.userRole, session?.subscription?.tier]);

  const hasAdditionalSetting = useMemo(() => {
    if (session?.userRole == "PLAYER" && additionalSettings && additionalSettings.length > 0) {
      return additionalSettings.every(setting => hasSetting(setting, true) !== false);
    } else {
      return true;
    }
  }, [hasSetting, additionalSettings, session?.userRole]);

  return hasScriptAccess && hasAdditionalSetting ? (
    <Page {...routeProps} envValues={envValues} Component={Component} />
  ) : (
    <Page {...routeProps} envValues={envValues} Component={NotFoundPage} />
  );
};

// 3. PlayerSettingsGuard
interface PlayerSettingsGuardProps {
  routeProps: any;
  envValues: EnvValues;
  session?: SocialCoachSession;
  Component: any;
  setting: CoachSettings;
  includeHeader?: boolean;
  includeFooter?: boolean;
}

export const PlayerSettingsGuard: React.FC<PlayerSettingsGuardProps> = ({
  routeProps,
  envValues,
  session,
  Component,
  setting,
  includeHeader = true,
  includeFooter = true,
}) => {
  const playerId = session?.playerId;
  const { hasSetting } = usePlayerSettings(playerId);
  const hasAccess = hasSetting(setting);

  if (hasAccess === false) {
    return <Page {...routeProps} envValues={envValues} Component={NotFoundPage} />;
  }

  return (
    <Page
      {...routeProps}
      envValues={envValues}
      Component={Component}
      includeHeader={includeHeader}
      includeFooter={includeFooter}
      pageLoading={hasAccess === undefined}
    />
  );
};

// 4. PlayerPlusGuard
interface PlayerPlusGuardProps {
  routeProps: any;
  envValues: EnvValues;
  session?: SocialCoachSession;
  Component: any;
}

export const PlayerPlusGuard: React.FC<PlayerPlusGuardProps> = ({ routeProps, envValues, session, Component }) => {
  const hasAccess = useMemo(() => {
    return session?.subscription?.tier === PricePlanTier.PLAYER_PLUS;
  }, [session?.subscription?.tier]);

  return hasAccess ? (
    <Page {...routeProps} envValues={envValues} Component={Component} />
  ) : (
    <Page {...routeProps} envValues={envValues} Component={NotFoundPage} />
  );
};
